import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
};
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-116">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" data-id="element-117">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={onClose} data-id="element-118" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden z-10" data-id="element-119">
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 shrink-0" data-id="element-120">
              <h2 className="text-2xl md:text-3xl font-playfair font-semibold text-charcoal" data-id="element-121">
                {title}
              </h2>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-pearl flex items-center justify-center hover:bg-gold/10 transition-colors" aria-label="Cerrar" data-id="element-122">
                <X size={20} className="text-charcoal" data-id="element-123" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-8 py-6 text-charcoal/80 text-sm md:text-base leading-relaxed space-y-6 font-montserrat" data-id="element-124">
              {children}
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-gray-100 shrink-0 flex justify-end" data-id="element-125">
              <button onClick={onClose} className="px-6 py-2.5 bg-gold hover:bg-gold-hover text-white rounded-full text-sm font-semibold transition-colors" data-id="element-126">
                Entendido
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
export function PrivacyContent() {
  return <>
      <p className="text-xs text-charcoal/50 uppercase tracking-widest font-semibold" data-id="element-127">
        Última actualización: Marzo 2026
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-128">
        1. Identidad del Responsable
      </h3>
      <p data-id="element-129">
        <strong data-id="element-130">Grand Town Inmobiliaria & Administración</strong>, con domicilio
        en Playa del Carmen, Quintana Roo, México, es responsable del
        tratamiento de sus datos personales conforme a la Ley Federal de
        Protección de Datos Personales en Posesión de los Particulares
        (LFPDPPP).
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-131">
        2. Datos Personales Recabados
      </h3>
      <p data-id="element-132">Podemos recabar los siguientes datos personales:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-133">
        <li data-id="element-134">Nombre completo</li>
        <li data-id="element-135">Número de teléfono y/o WhatsApp</li>
        <li data-id="element-136">Correo electrónico</li>
        <li data-id="element-137">Dirección de la propiedad</li>
        <li data-id="element-138">
          Información financiera relacionada con la administración de su
          inmueble
        </li>
      </ul>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-139">
        3. Finalidades del Tratamiento
      </h3>
      <p data-id="element-140">Sus datos personales serán utilizados para:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-141">
        <li data-id="element-142">
          Brindar servicios de administración de propiedades, compraventa y
          gestión de rentas.
        </li>
        <li data-id="element-143">
          Contactarle para dar seguimiento a solicitudes de diagnóstico o
          asesoría.
        </li>
        <li data-id="element-144">
          Enviar información relevante sobre el mercado inmobiliario de la
          Riviera Maya.
        </li>
        <li data-id="element-145">Cumplir con obligaciones legales y contractuales.</li>
      </ul>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-146">
        4. Transferencia de Datos
      </h3>
      <p data-id="element-147">
        Grand Town no transferirá sus datos personales a terceros sin su
        consentimiento, salvo en los casos previstos por la LFPDPPP y su
        Reglamento, o cuando sea necesario para el cumplimiento de obligaciones
        contractuales (por ejemplo, notarios, instituciones financieras o
        plataformas de renta).
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-148">
        5. Derechos ARCO
      </h3>
      <p data-id="element-149">
        Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al
        tratamiento de sus datos personales (Derechos ARCO). Para ejercer estos
        derechos, puede enviar una solicitud a nuestro correo electrónico:
        <strong data-id="element-150"> contacto@grandtown.com</strong>.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-151">
        6. Medidas de Seguridad
      </h3>
      <p data-id="element-152">
        Implementamos medidas de seguridad administrativas, técnicas y físicas
        para proteger sus datos personales contra daño, pérdida, alteración,
        destrucción o uso no autorizado.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-153">
        7. Cambios al Aviso de Privacidad
      </h3>
      <p data-id="element-154">
        Nos reservamos el derecho de modificar este Aviso de Privacidad.
        Cualquier cambio será comunicado a través de nuestro sitio web o por
        correo electrónico.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-155">
        8. Contacto
      </h3>
      <p data-id="element-156">
        Para cualquier duda o aclaración sobre este Aviso de Privacidad, puede
        contactarnos en:
        <br data-id="element-157" />
        <strong data-id="element-158">Email:</strong> contacto@grandtown.com
        <br data-id="element-159" />
        <strong data-id="element-160">Teléfono:</strong> +52 984 311 5669
        <br data-id="element-161" />
        <strong data-id="element-162">Ubicación:</strong> Playa del Carmen, Quintana Roo, México
      </p>
    </>;
}
export function TermsContent() {
  return <>
      <p className="text-xs text-charcoal/50 uppercase tracking-widest font-semibold" data-id="element-163">
        Última actualización: Marzo 2026
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-164">
        1. Aceptación de los Términos
      </h3>
      <p data-id="element-165">
        Al acceder y utilizar el sitio web de{' '}
        <strong data-id="element-166">Grand Town Inmobiliaria & Administración</strong>, usted acepta
        estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con
        alguno de ellos, le solicitamos no utilizar nuestros servicios.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-167">
        2. Descripción de los Servicios
      </h3>
      <p data-id="element-168">
        Grand Town ofrece los siguientes servicios inmobiliarios en la Riviera
        Maya:
      </p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-169">
        <li data-id="element-170">
          <strong data-id="element-171">Administración de Propiedades:</strong> Gestión integral
          incluyendo mantenimiento, relación con inquilinos y reportes
          financieros.
        </li>
        <li data-id="element-172">
          <strong data-id="element-173">Compraventa Estratégica:</strong> Asesoría para la adquisición
          y venta de inmuebles premium.
        </li>
        <li data-id="element-174">
          <strong data-id="element-175">Gestión de Rentas Premium:</strong> Maximización de ingresos
          por renta a través de plataformas selectas.
        </li>
      </ul>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-176">
        3. Obligaciones del Usuario
      </h3>
      <p data-id="element-177">El usuario se compromete a:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-178">
        <li data-id="element-179">Proporcionar información veraz y actualizada.</li>
        <li data-id="element-180">No utilizar el sitio web para fines ilícitos o no autorizados.</li>
        <li data-id="element-181">Respetar los derechos de propiedad intelectual de Grand Town.</li>
      </ul>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-182">
        4. Propiedad Intelectual
      </h3>
      <p data-id="element-183">
        Todo el contenido del sitio web, incluyendo textos, imágenes, logotipos,
        diseños y marcas, es propiedad exclusiva de Grand Town Inmobiliaria &
        Administración y está protegido por las leyes de propiedad intelectual
        de México. Queda prohibida su reproducción sin autorización previa por
        escrito.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-184">
        5. Limitación de Responsabilidad
      </h3>
      <p data-id="element-185">
        Grand Town no será responsable por daños directos, indirectos,
        incidentales o consecuentes derivados del uso o la imposibilidad de uso
        de este sitio web. La información proporcionada tiene carácter
        informativo y no constituye una oferta vinculante.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-186">
        6. Relación Contractual
      </h3>
      <p data-id="element-187">
        La contratación de cualquier servicio de Grand Town estará sujeta a la
        firma de un contrato específico entre las partes, el cual detallará los
        términos, condiciones, honorarios y obligaciones particulares de cada
        servicio.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-188">
        7. Legislación Aplicable
      </h3>
      <p data-id="element-189">
        Estos Términos y Condiciones se rigen por las leyes vigentes en los
        Estados Unidos Mexicanos. Para cualquier controversia derivada de los
        mismos, las partes se someten a la jurisdicción de los tribunales
        competentes de Playa del Carmen, Quintana Roo.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-190">
        8. Modificaciones
      </h3>
      <p data-id="element-191">
        Grand Town se reserva el derecho de modificar estos Términos y
        Condiciones en cualquier momento. Las modificaciones entrarán en vigor a
        partir de su publicación en el sitio web.
      </p>

      <h3 className="font-playfair text-lg font-semibold text-charcoal" data-id="element-192">
        9. Contacto
      </h3>
      <p data-id="element-193">
        Para cualquier consulta relacionada con estos Términos y Condiciones:
        <br data-id="element-194" />
        <strong data-id="element-195">Email:</strong> contacto@grandtown.com
        <br data-id="element-196" />
        <strong data-id="element-197">Teléfono:</strong> +52 984 311 5669
        <br data-id="element-198" />
        <strong data-id="element-199">Ubicación:</strong> Playa del Carmen, Quintana Roo, México
      </p>
    </>;
}